// src/pages/ProductDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import productService from '../services/productService';
import AddToCartModal from '../components/product/AddToCartModal';
import ProductImageGallery from '../components/product/ProductImageGallery';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import { formatCurrency } from '../utils/helpers';
import { CATEGORY_LABELS, getSizeLabel, sortBySizeOrder } from '../utils/constants';

/**
 * Product Detail Page Component
 * 
 * Features:
 * - Product images gallery
 * - Product details (name, price, description)
 * - College information
 * - Available sizes
 * - Stock status
 * - Add to cart with custom name ⭐
 * - Related products (same college)
 */
const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [showAddToCartModal, setShowAddToCartModal] = useState(false);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchProductDetails();
    window.scrollTo(0, 0);
  }, [id]);
  
  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const data = await productService.getProductById(id);
      setProduct(data);
      
      // Fetch related products from same college
      if (data.college?.id) {
        const related = await productService.getProductsByCollege(data.college.id);
        // Filter out current product and limit to 4
        const filtered = related
          .filter(p => p.id !== data.id)
          .slice(0, 4);
        setRelatedProducts(filtered);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      alert('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return <Loading fullScreen message="Loading product..." />;
  }
  
  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Product not found
          </h2>
          <Link to="/products">
            <Button>Browse Products</Button>
          </Link>
        </div>
      </div>
    );
  }
  
  const hasDiscount = product.discount && product.discount > 0;

  // The API returns price/stock PER SIZE (availableSizes) plus a top-level
  // startingPrice — there is no top-level finalPrice/price/stockQuantity.
  // Derive them the same way ProductCard does.
  const totalStock = product.availableSizes?.reduce(
    (sum, ps) => sum + (ps.stockQuantity || 0),
    0
  ) || 0;
  const isInStock = totalStock > 0;
  const discountedPrice = product.startingPrice != null
    ? ((100 - (product.discount || 0)) / 100) * product.startingPrice
    : null;
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-600 mb-6">
          <Link to="/" className="hover:text-blue-600">Home</Link>
          <span>/</span>
          <Link to="/products" className="hover:text-blue-600">Products</Link>
          <span>/</span>
          <span className="text-gray-900">{product.name}</span>
        </nav>
        
        {/* Product Details */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
            {/* Left Column - Images */}
            <div>
              <ProductImageGallery
                images={product.imageUrls || []}
                alt={product.name}
              />
            </div>
            
            {/* Right Column - Details */}
            <div>
              {/* College Badge */}
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
                  {product.college?.name}
                </span>
                {product.featured && (
                  <span className="bg-yellow-100 text-yellow-800 text-sm font-semibold px-3 py-1 rounded-full">
                    ⭐ Featured
                  </span>
                )}
              </div>
              
              {/* Product Name */}
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {product.name}
              </h1>
              
              {/* Category */}
              <p className="text-lg text-gray-600 mb-4">
                {CATEGORY_LABELS[product.category] || product.category}
              </p>
              
              {/* Price */}
              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl font-bold text-gray-900">
                  {formatCurrency(discountedPrice)}
                </span>
                {hasDiscount && (
                  <>
                    <span className="text-xl text-gray-500 line-through">
                      {formatCurrency(product.startingPrice)}
                    </span>
                    <span className="bg-red-500 text-white text-sm font-bold px-2 py-1 rounded">
                      {product.discount}% OFF
                    </span>
                  </>
                )}
              </div>
              
              {/* Stock Status */}
              <div className="mb-6">
                {isInStock ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                    <span className="font-medium">
                      {totalStock < 10
                        ? `Only ${totalStock} left in stock!`
                        : 'In Stock'
                      }
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-600">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                    </svg>
                    <span className="font-medium">Out of Stock</span>
                  </div>
                )}
              </div>
              
              {/* Available Sizes */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Available Sizes:
                </h3>
                <div className="flex flex-wrap gap-2">
                  {sortBySizeOrder(product.availableSizes).map(ps => (
                    <span
                      key={ps.id ?? ps.size}
                      className="bg-gray-100 text-gray-500 px-3 py-1 rounded-full text-sm cursor-default"
                    >
                      {getSizeLabel(ps.size)}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Custom Name Feature Highlight */}
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-6">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/>
                  </svg>
                  <div>
                    <p className="font-semibold text-blue-900">
                      Free Custom Name Printing! ⭐
                    </p>
                    <p className="text-sm text-blue-800 mt-1">
                      Personalize your {CATEGORY_LABELS[product.category]?.toLowerCase()} with custom name printing at no extra cost
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Add to Cart Button */}
              <Button
                size="lg"
                fullWidth
                onClick={() => setShowAddToCartModal(true)}
                disabled={!isInStock}
              >
                {isInStock ? 'Add to Cart' : 'Out of Stock'}
              </Button>
              
              {/* Product Info */}
              <div className="mt-8 space-y-4">
                {/* Description */}
                {product.description && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Description
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {product.description}
                    </p>
                  </div>
                )}
                
                {/* Product Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Product Details
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                      <span>College: {product.college?.name}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                      <span>Category: {CATEGORY_LABELS[product.category]}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                      <span>Material: Premium Cotton Blend</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                      <span>Printing: High-Quality Screen Print</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                      <span>Custom Name: Available (Max 50 characters)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              More from {product.college?.name}
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map(relatedProduct => (
                <Link
                  key={relatedProduct.id}
                  to={`/products/${relatedProduct.id}`}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <div className="aspect-square bg-gray-100">
                    <img
                      src={relatedProduct.imageUrls?.[0] || '/placeholder-product.png'}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                      {relatedProduct.name}
                    </h3>
                    <p className="text-lg font-bold text-gray-900">
                      {formatCurrency(((100 - (relatedProduct.discount || 0)) / 100) * relatedProduct.startingPrice)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Add to Cart Modal */}
      <AddToCartModal
        isOpen={showAddToCartModal}
        onClose={() => setShowAddToCartModal(false)}
        product={product}
      />
    </div>
  );
};

export default ProductDetailPage;